import {
  buildGroupedRows,
  isGroupHeaderRow,
  resolveGroupedRowsLoadMoreIndex,
  type GroupHeaderRowBase,
} from '@terra/ui-design-system';
import {
  EXTERNAL_ASSET_LIST_GROUP_BY_FIELDS,
  type AssetDetectionMetricsDto,
  type ExternalAssetListGroupDto,
  type ExternalAssetListOrderByDto,
  type ExternalAssetListSortField,
  type ExternalNetworkEntityKind,
} from '@terra/shared-contracts-external-network';
import type { ListPageTableSort } from '../../../common/list-page-infinite-table.model';
import type { EnetAssetListCellValues } from '../asset-list-cell-values';

export const EXTERNAL_ASSETS_PAGE_SIZE = 50;
export const EXTERNAL_ASSETS_GROUP_HEADER_ID_PREFIX =
  '__external_assets_group__:';
export const EXTERNAL_ASSETS_EMPTY_GROUP_KEY = '';

export type ExternalAssetsAllKindsPageParam = {
  entityKind: ExternalNetworkEntityKind;
  skip: number;
};

export type ExternalAssetsAllKindsPage = ExternalAssetsAllKindsPageParam & {
  data: readonly object[];
  total: number;
};

export const resolveFirstAllKindsPageParam = ({
  kinds,
  supportsKind,
}: {
  kinds: readonly ExternalNetworkEntityKind[];
  supportsKind: (kind: ExternalNetworkEntityKind) => boolean;
}): ExternalAssetsAllKindsPageParam | undefined => {
  const entityKind = kinds.find(supportsKind);

  return entityKind ? { entityKind, skip: 0 } : undefined;
};

export const resolveNextAllKindsPageParam = ({
  kinds,
  supportsKind,
  lastPage,
}: {
  kinds: readonly ExternalNetworkEntityKind[];
  supportsKind: (kind: ExternalNetworkEntityKind) => boolean;
  lastPage: ExternalAssetsAllKindsPageParam & {
    rowCount: number;
    total: number;
  };
}): ExternalAssetsAllKindsPageParam | undefined => {
  const loadedCount = lastPage.skip + lastPage.rowCount;

  if (lastPage.rowCount > 0 && loadedCount < lastPage.total) {
    return { entityKind: lastPage.entityKind, skip: loadedCount };
  }

  const nextKind = kinds
    .slice(kinds.indexOf(lastPage.entityKind) + 1)
    .find(supportsKind);

  return nextKind ? { entityKind: nextKind, skip: 0 } : undefined;
};

export type ExternalAssetGridItem = {
  id: string;
  rowId: string;
  entityKind: ExternalNetworkEntityKind;
  label: string;
  cells: EnetAssetListCellValues;
  metrics: AssetDetectionMetricsDto | undefined;
};

export type ExternalAssetsGroupHeaderRow = GroupHeaderRowBase & {
  entityKind: ExternalNetworkEntityKind | null;
};

export type ExternalAssetsTableRow =
  | ExternalAssetGridItem
  | ExternalAssetsGroupHeaderRow;

export const isExternalAssetsGroupHeaderRow = (
  row: ExternalAssetsTableRow | null | undefined,
): row is ExternalAssetsGroupHeaderRow => isGroupHeaderRow(row);

export const EXTERNAL_ASSETS_SORT_COLUMNS = [
  'name',
  'signals',
  'findings',
] as const satisfies readonly ExternalAssetListSortField[];

export type ExternalAssetsSortColumn =
  (typeof EXTERNAL_ASSETS_SORT_COLUMNS)[number];

export type ExternalAssetsSort = ListPageTableSort<ExternalAssetsSortColumn>;

export const isExternalAssetsSortColumn = (
  colId: string,
): colId is ExternalAssetsSortColumn =>
  EXTERNAL_ASSETS_SORT_COLUMNS.some((column) => column === colId);

export const toExternalAssetsOrderBy = (
  sort: ExternalAssetsSort,
): ExternalAssetListOrderByDto[] | undefined =>
  sort ? [{ field: sort.colId, order: sort.sort }] : undefined;

export type ExternalAssetsGroupByField =
  (typeof EXTERNAL_ASSET_LIST_GROUP_BY_FIELDS)[ExternalNetworkEntityKind][number];

const NO_GROUP_BY_OPTIONS: readonly ExternalAssetsGroupByField[] = [];

export const resolveExternalAssetsGroupByOptions = (
  kind: ExternalNetworkEntityKind | null,
): readonly ExternalAssetsGroupByField[] =>
  kind ? EXTERNAL_ASSET_LIST_GROUP_BY_FIELDS[kind] : NO_GROUP_BY_OPTIONS;

export const resolveExternalAssetsGroupBy = ({
  kind,
  groupBy,
}: {
  kind: ExternalNetworkEntityKind | null;
  groupBy: ExternalAssetsGroupByField | null;
}): ExternalAssetsGroupByField | undefined =>
  resolveExternalAssetsGroupByOptions(kind).find((field) => field === groupBy);

export const assignServerGroupKeys = ({
  items,
  groups,
}: {
  items: readonly ExternalAssetGridItem[];
  groups: readonly ExternalAssetListGroupDto[];
}): ReadonlyMap<string, string> => {
  const boundaries = groups.reduce<Array<{ key: string; end: number }>>(
    (acc, group) => [
      ...acc,
      {
        key: group.key ?? EXTERNAL_ASSETS_EMPTY_GROUP_KEY,
        end: (acc.at(-1)?.end ?? 0) + group.count,
      },
    ],
    [],
  );

  return new Map(
    items.map((item, index) => [
      item.rowId,
      boundaries.find((boundary) => index < boundary.end)?.key ??
        EXTERNAL_ASSETS_EMPTY_GROUP_KEY,
    ]),
  );
};

export type ExternalAssetsGrouping =
  | { mode: 'none' }
  | {
      mode: 'kind';
      kinds: readonly ExternalNetworkEntityKind[];
      totals: ReadonlyMap<string, number>;
    }
  | { mode: 'field'; groups: readonly ExternalAssetListGroupDto[] };

export const buildExternalAssetsTableRows = ({
  items,
  grouping,
  collapsedGroupKeys,
  getGroupLabel,
}: {
  items: readonly ExternalAssetGridItem[];
  grouping: ExternalAssetsGrouping;
  collapsedGroupKeys: ReadonlySet<string>;
  getGroupLabel: (groupKey: string) => string;
}): ExternalAssetsTableRow[] => {
  if (grouping.mode === 'none') {
    return [...items];
  }

  if (grouping.mode === 'kind') {
    return buildGroupedRows<
      ExternalAssetGridItem,
      ExternalAssetsGroupHeaderRow
    >({
      items,
      getItemId: (item) => item.rowId,
      getGroupKey: (item) => item.entityKind,
      getGroupLabel,
      collapsedGroupKeys,
      headerIdPrefix: EXTERNAL_ASSETS_GROUP_HEADER_ID_PREFIX,
      knownGroupKeys: grouping.kinds.filter(
        (kind) => (grouping.totals.get(kind) ?? 0) > 0,
      ),
      decorateHeader: (header) => ({
        ...header,
        count: grouping.totals.get(header.groupKey) ?? header.count,
        entityKind:
          grouping.kinds.find((kind) => kind === header.groupKey) ?? null,
      }),
    });
  }

  const groupKeyByRowId = assignServerGroupKeys({
    items,
    groups: grouping.groups,
  });
  const countByKey = new Map(
    grouping.groups.map((group) => [
      group.key ?? EXTERNAL_ASSETS_EMPTY_GROUP_KEY,
      group.count,
    ]),
  );

  return buildGroupedRows<ExternalAssetGridItem, ExternalAssetsGroupHeaderRow>({
    items,
    getItemId: (item) => item.rowId,
    getGroupKey: (item) =>
      groupKeyByRowId.get(item.rowId) ?? EXTERNAL_ASSETS_EMPTY_GROUP_KEY,
    getGroupLabel,
    collapsedGroupKeys,
    headerIdPrefix: EXTERNAL_ASSETS_GROUP_HEADER_ID_PREFIX,
    knownGroupKeys: [...countByKey.keys()],
    decorateHeader: (header) => ({
      ...header,
      count: countByKey.get(header.groupKey) ?? header.count,
      entityKind: null,
    }),
  });
};

export const resolveExternalAssetsLoadMoreRowIndex = ({
  rows,
  grouping,
  collapsedGroupKeys,
}: {
  rows: readonly ExternalAssetsTableRow[];
  grouping: ExternalAssetsGrouping;
  collapsedGroupKeys: ReadonlySet<string>;
}): number | null => {
  switch (grouping.mode) {
    case 'none':
      return rows.length - 1;
    case 'kind':
    case 'field':
      return resolveGroupedRowsLoadMoreIndex({
        rows,
        collapsedGroupKeys,
        hasUnseenGroups: false,
      });
  }
};
